// Client-side image compression for chat attachments.
//
// Tuned for SCREENSHOTS, which are the common case here and the hardest: they
// contain small text that turns to mush under aggressive JPEG compression. So:
//   * generous max dimension (1920px) - downscaling is what destroys text
//   * high quality (0.85) rather than the usual 0.6-0.7
//   * skip entirely when the file is already small
//   * keep PNG when the image has transparency (JPEG would fill it black)
// A 4 MB screenshot typically lands around 300-600 KB and stays crisp.

const MAX_DIMENSION = 1920;
const QUALITY = 0.85;
const SKIP_BELOW_BYTES = 400 * 1024; // already small enough to send as-is

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
    img.src = url;
  });
}

/** True when any pixel is non-opaque (sampled) - such images must stay PNG. */
function hasTransparency(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): boolean {
  try {
    const { width, height } = canvas;
    const step = Math.max(1, Math.floor(Math.min(width, height) / 64));
    const data = ctx.getImageData(0, 0, width, height).data;
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        if (data[(y * width + x) * 4 + 3] < 250) return true;
      }
    }
  } catch {
    return true; // tainted canvas - be safe and keep PNG
  }
  return false;
}

/**
 * Returns a compressed File, or the original when compression wouldn't help.
 * Never throws: on any failure the original file is used.
 */
export async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;          // would lose animation
  if (file.size <= SKIP_BELOW_BYTES) return file;

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // Smooth downscaling keeps glyph edges legible.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    const keepPng = file.type === 'image/png' && hasTransparency(canvas, ctx);
    const mime = keepPng ? 'image/png' : 'image/jpeg';

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, mime, keepPng ? undefined : QUALITY));
    if (!blob || blob.size >= file.size) return file; // compression didn't help

    const name = file.name.replace(/\.[^.]+$/, '') + (keepPng ? '.png' : '.jpg');
    return new File([blob], name, { type: mime, lastModified: Date.now() });
  } catch {
    return file;
  }
}

/** Pulls an image out of a paste event (screenshots land here as a blob). */
export function imageFromClipboard(event: React.ClipboardEvent): File | null {
  const items = event.clipboardData?.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (!file) continue;
      // Clipboard images are usually named "image.png" or nothing at all.
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      return new File([file], file.name && file.name !== 'image.png' ? file.name : `screenshot-${stamp}.${ext}`, {
        type: item.type,
      });
    }
  }
  return null;
}
